import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchThetaOptionHistoryGreeksAll, isThetaDataAvailable } from '../services/thetaDataService';
import type { ThetaOptionHistoryGreeksAllRow } from '../services/thetaDataService';
import type {
  ContractHistoricalParams,
  EodRecord,
  EodTodaySnapshot,
  HistoricalSource,
  TapeByDayRow,
  TapeSummary,
} from '../types/contractHistorical';
import { EOD_MAX_PAGES, RELATIVE_UNUSUAL_THRESHOLD } from '../types/contractHistorical';
import {
  buildTapeByDayUrl,
  clampDateRange,
  fetchEodPaginated,
  resolveFuturesUnderlying,
  thirtyDaysAgo,
} from '../utils/contractHistoricalEod';
import { chartFiniteNumber } from '../utils/contractHistoricalIntraday';
import { liveOiReading } from '../utils/liveSessionOi';
import { openCloseBounds } from '../utils/openCloseBounds';

export interface UseContractHistoricalEodParams {
  params: ContractHistoricalParams | null;
  enabled: boolean;
  activeTab: 'eod' | 'intraday' | 'flow';
  source?: HistoricalSource;
}

export interface EodChartDayRow {
  date: string;
  volume: number;
  open_interest: number;
  bid: number;
  ask: number;
  bid_size: number;
  ask_size: number;
  midpoint: number;
  last: number;
  vol_oi: number;
  premium: number;
  delta: number | null;
  gamma: number | null;
  spot_gamma: number | null;
  theta: number | null;
  vega: number | null;
  volatility: number | null;
  /** Set on the in-progress session only, so the UI can mark it as still moving. */
  is_today?: boolean;
  /** The session the open interest was published for -- never today. */
  open_interest_as_of?: string | null;
  /** Contracts today's volume PROVES are new; null when the arithmetic forces nothing. */
  min_new_contracts?: number | null;
  /** Where tomorrow's published open interest must land. */
  oi_floor?: number | null;
  oi_ceiling?: number | null;
}

export interface ThetaSecondOrderRow {
  date: string;
  vanna: number;
  charm: number;
  vomma: number;
  veta: number;
  implied_vol: number;
}

export interface ThetaThirdOrderRow {
  date: string;
  speed: number;
  color: number;
  ultima: number;
}

export interface EodAverages {
  volume: number;
  bid_size: number;
  ask_size: number;
  premium: number;
  open_interest: number;
}

export interface UnusualFlowRow {
  date: string;
  metric: string;
  value: number;
  avg: number;
  ratio: number;
}

export interface EodChartPoint {
  date: string;
  value: number;
}

export interface BoughtSoldPctRow {
  date: string;
  boughtPct: number;
  soldPct: number;
  total: number;
}

export interface OpenClosePctRow {
  date: string;
  volume: number;
  /** Published change in open interest; null when it cannot be known yet. */
  deltaOi: number | null;
  /** Share of the session's volume that MUST have opened. Null when nothing is provable. */
  openMinPct: number | null;
  /** The most that COULD have opened -- the figure the old estimate drew as the answer. */
  openMaxPct: number | null;
  closeMinPct: number | null;
  closeMaxPct: number | null;
  /** Band widths, for the shaded range between floor and ceiling. */
  openRangePct: number | null;
  closeRangePct: number | null;
  inconsistent: boolean;
  unresolved: boolean;
}

export interface FlowChartRow {
  date: string;
  boughtCall: number;
  soldCall: number;
  boughtPut: number;
  soldPut: number;
}

export interface TapeByDayChartRow {
  date: string;
  dateFull: string;
  atBid: number;
  atAsk: number;
  between: number;
}

function buildChartData(data: EodRecord[], isPut: boolean): EodChartDayRow[] {
  const byDay = new Map<string, EodRecord[]>();
  for (const r of data) {
    const date = (r.tradetime || '').slice(0, 10);
    if (!date) continue;
    if (!byDay.has(date)) byDay.set(date, []);
    byDay.get(date)!.push(r);
  }
  const dates = Array.from(byDay.keys()).sort((a, b) => a.localeCompare(b));
  return dates.map((date) => {
    const rows = byDay.get(date)!;
    const sorted = [...rows].sort((a, b) => (a.tradetime || '').localeCompare(b.tradetime || ''));
    const r = sorted[sorted.length - 1];
    const vol = sorted.reduce((s, x) => s + (Number(x.volume) || 0), 0);
    const bid_size = sorted.reduce((s, x) => s + (Number(x.bid_size) || 0), 0);
    const ask_size = sorted.reduce((s, x) => s + (Number(x.ask_size) || 0), 0);
    const mid = Number(r.midpoint) || Number(r.last) || (Number(r.bid) + Number(r.ask)) / 2 || 0;
    const last = Number(r.last) ?? mid;
    const premium = vol * 100 * mid;
    const gamma = r.gamma != null ? Number(r.gamma) : null;
    const oi = Number(r.open_interest) || 0;
    const vol_oi = oi > 0 ? vol / oi : 0;
    const dir = isPut ? -1 : 1;
    const spot_gamma =
      r.spot_gamma != null && Number(r.spot_gamma) !== 0
        ? Number(r.spot_gamma)
        : gamma != null && oi > 0
          ? gamma * oi * 100 * dir
          : null;
    return {
      date,
      volume: vol,
      open_interest: oi,
      bid: Number(r.bid) || 0,
      ask: Number(r.ask) || 0,
      bid_size,
      ask_size,
      midpoint: mid,
      last,
      vol_oi,
      premium,
      delta: r.delta != null ? Number(r.delta) : null,
      gamma,
      spot_gamma,
      theta: r.theta != null ? Number(r.theta) : null,
      vega: r.vega != null ? Number(r.vega) : null,
      volatility: r.volatility != null ? Number(r.volatility) * 100 : null,
    };
  });
}

function primaryMetricKeyFromSource(source: HistoricalSource): keyof EodChartDayRow {
  switch (source) {
    case 'bid':
      return 'bid_size';
    case 'ask':
      return 'ask_size';
    case 'oi':
      return 'open_interest';
    case 'trades':
    case 'premium':
      return 'premium';
    case 'delta':
      return 'delta';
    case 'gamma':
    case 'mal':
      return 'gamma';
    case 'spot_gamma':
      return 'spot_gamma';
    case 'theta':
      return 'theta';
    case 'vega':
      return 'volatility';
    case 'last':
      return 'last';
    case 'mid':
      return 'midpoint';
    case 'vol_oi':
      return 'vol_oi';
    case 'haltz':
    case 'zahn':
    case 'rho':
    case 'lambda':
    case 'theoretical':
      return 'volume';
    default:
      return 'volume';
  }
}

function primaryLabelFromSource(source: HistoricalSource): string {
  switch (source) {
    case 'bid':
      return 'Bid size';
    case 'ask':
      return 'Ask size';
    case 'oi':
      return 'Open interest';
    case 'trades':
    case 'premium':
      return 'Premium flow (vol × 100 × mid)';
    case 'delta':
      return 'Delta';
    case 'gamma':
      return 'Gamma';
    case 'mal':
      return 'Gamma (opened from Mal)';
    case 'spot_gamma':
      return 'Spot Gamma';
    case 'theta':
      return 'Theta';
    case 'vega':
      return 'Implied volatility (%)';
    case 'last':
      return 'Last price';
    case 'mid':
      return 'Midpoint';
    case 'vol_oi':
      return 'Volume / Open interest (ratio)';
    case 'haltz':
      return 'Volume (opened from Haltz)';
    case 'zahn':
      return 'Volume (opened from Zahn)';
    case 'rho':
      return 'Volume (opened from Rho)';
    case 'lambda':
      return 'Volume (opened from Lambda)';
    case 'theoretical':
      return 'Volume (opened from Theo Value)';
    default:
      return 'Volume';
  }
}

export function useContractHistoricalEod({
  params,
  enabled,
  activeTab,
  source: sourceOverride,
}: UseContractHistoricalEodParams) {
  const [data, setData] = useState<EodRecord[]>([]);
  const [callEod, setCallEod] = useState<EodRecord[]>([]);
  const [putEod, setPutEod] = useState<EodRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultFrom = useMemo(() => thirtyDaysAgo(), []);
  const minDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: defaultFrom, to: today });

  const [tapeByDay, setTapeByDay] = useState<TapeByDayRow[]>([]);
  const [tapeByDayLoading, setTapeByDayLoading] = useState(false);
  const [todaySnapshot, setTodaySnapshot] = useState<EodTodaySnapshot | null>(null);

  const [thetaSecondOrder, setThetaSecondOrder] = useState<ThetaSecondOrderRow[]>([]);
  const [thetaThirdOrder, setThetaThirdOrder] = useState<ThetaThirdOrderRow[]>([]);
  const [loadingThetaGreeks, setLoadingThetaGreeks] = useState(false);

  const effectiveSource = sourceOverride ?? params?.source ?? 'volume';
  const isPut = params?.optionType?.toLowerCase() === 'put';

  const fetchForRange = useCallback(
    (from: string, to: string) => {
      if (!params?.underlying || params.strike === undefined) return;
      const clamped = clampDateRange(from, to);
      setDateRange(clamped);
      setLoading(true);
      setError(null);
      Promise.all([
        fetchEodPaginated(params, clamped.from, clamped.to, 'call'),
        fetchEodPaginated(params, clamped.from, clamped.to, 'put'),
      ])
        .then(([resCall, resPut]) => {
          const listCall = resCall.list;
          const listPut = resPut.list;
          setCallEod(listCall);
          setPutEod(listPut);
          const primaryIsPut = params?.optionType?.toLowerCase() === 'put';
          setData(primaryIsPut ? listPut : listCall);
          // Capture today's live snapshot from the response (injected by backend)
          const snap = resCall.todaySnapshot ?? resPut.todaySnapshot ?? null;
          setTodaySnapshot(snap);
          if (listCall.length === 0 && listPut.length === 0) {
            const msg = resCall.serverError || resPut.serverError;
            setError(msg || 'Historical data is temporarily unavailable.');
          }
        })
        .catch(() => {
          setError('Historical data is temporarily unavailable.');
          setData([]);
          setCallEod([]);
          setPutEod([]);
        })
        .finally(() => setLoading(false));
    },
    [params],
  );

  const handleUpdateDates = useCallback(() => {
    fetchForRange(dateRange.from, dateRange.to);
  }, [fetchForRange, dateRange.from, dateRange.to]);

  const tapeByDayUrl = useMemo(() => {
    if (!params?.underlying || params.strike == null || !params.expiry || params.expiry === 'all') {
      return null;
    }
    return buildTapeByDayUrl(params, dateRange.from || defaultFrom, dateRange.to || today);
  }, [params, dateRange.from, dateRange.to, defaultFrom, today]);

  /** Initial EOD load when contract opens or changes. */
  useEffect(() => {
    if (!enabled || !params?.underlying || params.strike === undefined) {
      setData([]);
      setCallEod([]);
      setPutEod([]);
      setLoading(false);
      setError(null);
      return;
    }
    setData([]);
    setCallEod([]);
    setPutEod([]);
    setError(null);
    setDateRange({ from: defaultFrom, to: today });
    setLoading(true);
    const clamped = clampDateRange(defaultFrom, today);
    const primaryIsPut = params?.optionType?.toLowerCase() === 'put';
    const primaryType: 'call' | 'put' = primaryIsPut ? 'put' : 'call';
    const secondaryType: 'call' | 'put' = primaryIsPut ? 'call' : 'put';

    fetchEodPaginated(params, clamped.from, clamped.to, primaryType, EOD_MAX_PAGES)
      .then((resPrimary) => {
        const list = resPrimary.list;
        if (primaryIsPut) {
          setPutEod(list);
        } else {
          setCallEod(list);
        }
        setData(list);
        if (resPrimary.todaySnapshot) setTodaySnapshot(resPrimary.todaySnapshot);
        if (list.length === 0) {
          setError(resPrimary.serverError || 'Historical data is temporarily unavailable.');
        }
        if (list.length > 0) {
          const dates = list.map((r) => (r.tradetime || '').slice(0, 10)).filter(Boolean);
          const from = dates[0] || defaultFrom;
          const to = dates[dates.length - 1] || today;
          setDateRange((prev) => ({ from: prev.from || from, to: prev.to || to }));
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Historical data is temporarily unavailable.');
        setData([]);
        setLoading(false);
      });

    fetchEodPaginated(params, clamped.from, clamped.to, secondaryType, EOD_MAX_PAGES)
      .then((resSecondary) => {
        if (primaryIsPut) {
          setCallEod(resSecondary.list);
        } else {
          setPutEod(resSecondary.list);
        }
        if (resSecondary.todaySnapshot && !todaySnapshot) setTodaySnapshot(resSecondary.todaySnapshot);
      })
      .catch(() => {
        /* keep primary data on background failure */
      });
  }, [enabled, params?.underlying, params?.strike, params?.expiry, params?.optionType, defaultFrom, today, params]);

  /** Fetch print classification by day for EOD tab. */
  useEffect(() => {
    if (activeTab !== 'eod' || !enabled || !tapeByDayUrl) {
      setTapeByDay([]);
      setTapeByDayLoading(false);
      return;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 180_000);
    setTapeByDayLoading(true);
    fetch(tapeByDayUrl, { signal: ac.signal })
      .then((r) => r.json())
      .then((json) => {
        if (json?.success && Array.isArray(json.days)) {
          setTapeByDay(json.days as TapeByDayRow[]);
        } else {
          setTapeByDay([]);
        }
      })
      .catch(() => {
        setTapeByDay([]);
      })
      .finally(() => {
        clearTimeout(timer);
        setTapeByDayLoading(false);
      });
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [activeTab, enabled, tapeByDayUrl]);

  /** Fetch Theta second- and third-order Greeks; prefer one bar per day (24h), fall back to 1m series if daily is empty. */
  useEffect(() => {
    if (
      !enabled ||
      !params?.underlying ||
      params.strike === undefined ||
      !params?.expiry ||
      params.expiry === 'all' ||
      activeTab !== 'eod' ||
      !isThetaDataAvailable()
    ) {
      setThetaSecondOrder([]);
      setThetaThirdOrder([]);
      return;
    }
    const from = (dateRange.from || defaultFrom).replace(/-/g, '').slice(0, 8);
    const to = (dateRange.to || today).replace(/-/g, '').slice(0, 8);
    const expStr = String(params.expiry || '').replace(/-/g, '').slice(0, 8);
    if (!expStr || from > to) return;
    const root = resolveFuturesUnderlying(params.underlying.trim());
    const strike = Number(params.strike);
    const right = (params.optionType?.toLowerCase() === 'put' ? 'P' : 'C') as 'C' | 'P';
    const baseParams = { root, startDate: from, endDate: to, exp: expStr, strike, right };
    const dayMs = 24 * 60 * 60 * 1000;
    let cancelled = false;
    setLoadingThetaGreeks(true);
    (async () => {
      let merged: ThetaOptionHistoryGreeksAllRow[] = [];
      try {
        merged = await fetchThetaOptionHistoryGreeksAll({ ...baseParams, intervalMs: dayMs }).catch(() => []);
        if (merged.length === 0) {
          merged = await fetchThetaOptionHistoryGreeksAll(baseParams).catch(() => []);
        }
      } catch {
        merged = [];
      }
      if (cancelled) return;
      const validYmd = (d: number) => d >= 20000101 && d <= 21001231;
      const ymdToIso = (d: number) =>
        `${String(Math.floor(d / 10000))}-${String(Math.floor((d % 10000) / 100)).padStart(2, '0')}-${String(d % 100).padStart(2, '0')}`;

      const byDateSecond = new Map<number, ThetaOptionHistoryGreeksAllRow>();
      merged.forEach((row) => {
        const d = row.date;
        if (!validYmd(d)) return;
        const existing = byDateSecond.get(d);
        if (!existing || row.ms_of_day >= (existing?.ms_of_day ?? 0)) byDateSecond.set(d, row);
      });
      const secondAgg = Array.from(byDateSecond.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([d, row]) => ({
          date: ymdToIso(d),
          vanna: chartFiniteNumber(row.vanna),
          charm: chartFiniteNumber(row.charm),
          vomma: chartFiniteNumber(row.vomma),
          veta: chartFiniteNumber(row.veta),
          implied_vol: chartFiniteNumber(row.implied_vol),
        }));
      setThetaSecondOrder(secondAgg);

      const byDateThird = new Map<number, ThetaOptionHistoryGreeksAllRow>();
      merged.forEach((row) => {
        const d = row.date;
        if (!validYmd(d)) return;
        const existing = byDateThird.get(d);
        if (!existing || (row.ms_of_day ?? 0) >= (existing?.ms_of_day ?? 0)) byDateThird.set(d, row);
      });
      const thirdAgg = Array.from(byDateThird.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([d, row]) => ({
          date: ymdToIso(d),
          speed: chartFiniteNumber(row.speed),
          color: chartFiniteNumber(row.color),
          ultima: chartFiniteNumber(row.ultima),
        }));
      setThetaThirdOrder(thirdAgg);
    })().finally(() => {
      if (!cancelled) setLoadingThetaGreeks(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    params?.underlying,
    params?.strike,
    params?.expiry,
    params?.optionType,
    dateRange.from,
    dateRange.to,
    activeTab,
    defaultFrom,
    today,
  ]);

  const chartData = useMemo(() => {
    const rows = buildChartData(data, Boolean(isPut));
    if (!todaySnapshot) return rows;

    // Open interest is published once, after the close; the last completed session holds
    // the only real figure there is. `|| 0` used to invent one, which made vol/OI and the
    // opening-vs-closing read downstream of it wrong rather than absent.
    const prior = rows.length ? rows[rows.length - 1] : null;
    const reading = liveOiReading({
      snapshotOi: todaySnapshot.open_interest,
      snapshotVolume: todaySnapshot.volume,
      priorOi: prior?.open_interest,
      priorDate: prior?.date,
      // The row's OI column reflects the session BEFORE it (published the next morning).
      priorOiAsOf: rows.length > 1 ? rows[rows.length - 2]?.date : undefined,
    });

    // Nothing has printed yet: outside RTH there is no session to show, and a row of
    // zeroes reads as a contract nobody traded rather than a market that is shut.
    if (reading.state === 'pending' || reading.volume === null) return rows;

    const last = todaySnapshot.last || 0;
    const bid = todaySnapshot.bid || 0;
    const ask = todaySnapshot.ask || 0;
    const iv = todaySnapshot.implied_volatility || 0;
    const delta = todaySnapshot.delta || 0;
    const gamma = todaySnapshot.gamma || 0;
    const oi = reading.openInterest;
    const dir = isPut ? -1 : 1;
    const spot_gamma = gamma && oi ? gamma * oi * 100 * dir : null;
    const mid = (bid + ask) / 2 || last;
    rows.push({
      date: today,
      volume: reading.volume,
      open_interest: oi ?? 0,
      bid,
      ask,
      bid_size: 0,
      ask_size: 0,
      midpoint: mid,
      last,
      vol_oi: oi ? reading.volume / oi : 0,
      premium: reading.volume * 100 * mid,
      delta,
      gamma,
      spot_gamma,
      theta: null,
      vega: null,
      volatility: iv * 100 || null,
      is_today: true,
      open_interest_as_of: reading.openInterestAsOf,
      min_new_contracts: reading.minNewContracts,
      oi_floor: reading.oiFloor,
      oi_ceiling: reading.oiCeiling,
    });
    return rows;
  }, [data, isPut, todaySnapshot, today]);

  const filteredByDate = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return chartData;
    return chartData.filter((d) => {
      if (dateRange.from && d.date < dateRange.from) return false;
      if (dateRange.to && d.date > dateRange.to) return false;
      return true;
    });
  }, [chartData, dateRange]);

  const averages = useMemo((): EodAverages => {
    // Completed sessions only. Today is a PARTIAL session, so averaging it in drags the
    // baseline down all morning and then lets the same row be flagged unusual against a
    // baseline it is itself part of.
    const completed = filteredByDate.filter((r) => !r.is_today);
    if (completed.length === 0) {
      return { volume: 0, bid_size: 0, ask_size: 0, premium: 0, open_interest: 0 };
    }
    const sum = (key: keyof EodChartDayRow) =>
      completed.reduce((a, r) => a + (Number(r[key]) || 0), 0);
    const n = completed.length;
    return {
      volume: sum('volume') / n,
      bid_size: sum('bid_size') / n,
      ask_size: sum('ask_size') / n,
      premium: sum('premium') / n,
      open_interest: sum('open_interest') / n,
    };
  }, [filteredByDate]);

  const unusualFlow = useMemo((): UnusualFlowRow[] => {
    const list: UnusualFlowRow[] = [];
    const push = (date: string, metric: string, value: number, avg: number) => {
      if (avg <= 0) return;
      const ratio = value / avg;
      if (ratio >= RELATIVE_UNUSUAL_THRESHOLD) list.push({ date, metric, value, avg, ratio });
    };
    filteredByDate.forEach((r) => {
      push(r.date, 'Volume', r.volume, averages.volume);
      push(r.date, 'Bid size', r.bid_size, averages.bid_size);
      push(r.date, 'Ask size', r.ask_size, averages.ask_size);
      push(r.date, 'Premium flow', r.premium, averages.premium);
      push(r.date, 'Open interest', r.open_interest, averages.open_interest);
    });
    list.sort((a, b) => a.date.localeCompare(b.date));
    return list;
  }, [filteredByDate, averages]);

  const primaryMetricKey = primaryMetricKeyFromSource(effectiveSource);
  const primaryLabel = primaryLabelFromSource(effectiveSource);

  const primaryChartData = useMemo(
    (): EodChartPoint[] =>
      filteredByDate.map((r) => ({
        date: r.date,
        value: chartFiniteNumber(r[primaryMetricKey]),
      })),
    [filteredByDate, primaryMetricKey],
  );

  const tradesChartData = useMemo(
    (): EodChartPoint[] => filteredByDate.map((r) => ({ date: r.date, value: chartFiniteNumber(r.premium) })),
    [filteredByDate],
  );

  const gammaChartData = useMemo(
    (): EodChartPoint[] =>
      filteredByDate
        .filter((r) => r.gamma != null && Number.isFinite(Number(r.gamma)))
        .map((r) => ({ date: r.date, value: chartFiniteNumber(r.gamma) })),
    [filteredByDate],
  );

  const spotGammaChartData = useMemo(
    (): EodChartPoint[] =>
      filteredByDate
        .filter((r) => r.spot_gamma != null && Number.isFinite(Number(r.spot_gamma)))
        .map((r) => ({ date: r.date, value: chartFiniteNumber(r.spot_gamma) })),
    [filteredByDate],
  );

  const deltaChartData = useMemo(
    (): EodChartPoint[] =>
      filteredByDate
        .filter((r) => r.delta != null && Number.isFinite(Number(r.delta)))
        .map((r) => ({ date: r.date, value: chartFiniteNumber(r.delta) })),
    [filteredByDate],
  );

  const volatilityChartData = useMemo(
    (): EodChartPoint[] =>
      filteredByDate
        .filter((r) => r.volatility != null && Number.isFinite(Number(r.volatility)))
        .map((r) => ({ date: r.date, value: chartFiniteNumber(r.volatility) })),
    [filteredByDate],
  );

  const boughtSoldPctData = useMemo((): BoughtSoldPctRow[] => {
    return filteredByDate.map((r) => {
      const bid = r.bid_size ?? 0;
      const ask = r.ask_size ?? 0;
      const total = bid + ask;
      const boughtPct = total > 0 ? (100 * ask) / total : 50;
      const soldPct = total > 0 ? (100 * bid) / total : 50;
      return { date: r.date, boughtPct, soldPct, total: bid + ask };
    });
  }, [filteredByDate]);

  // Bounds, not a point split: the mixed volume where one side opened against the other
  // closing never touches open interest, so it is invisible in the daily change and the
  // exact split is not recoverable. See utils/openCloseBounds.ts.
  const openClosePctData = useMemo(
    (): OpenClosePctRow[] => openCloseBounds(filteredByDate),
    [filteredByDate],
  );

  const flowChartData = useMemo((): FlowChartRow[] => {
    const accumulate = (rows: EodRecord[]) => {
      const m = new Map<string, { ask: number; bid: number }>();
      for (const r of rows) {
        const d = (r.tradetime || '').slice(0, 10);
        if (!d) continue;
        const cur = m.get(d) || { ask: 0, bid: 0 };
        cur.ask += Number(r.ask_size) || 0;
        cur.bid += Number(r.bid_size) || 0;
        m.set(d, cur);
      }
      return m;
    };
    const callByDate = accumulate(callEod);
    const putByDate = accumulate(putEod);
    const dates = new Set<string>([...Array.from(callByDate.keys()), ...Array.from(putByDate.keys())]);
    let list = Array.from(dates)
      .filter((d) => d)
      .map((date) => {
        const c = callByDate.get(date) || { ask: 0, bid: 0 };
        const p = putByDate.get(date) || { ask: 0, bid: 0 };
        return {
          date,
          boughtCall: c.ask,
          soldCall: c.bid,
          boughtPut: p.ask,
          soldPut: p.bid,
        };
      });
    list.sort((a, b) => a.date.localeCompare(b.date));
    if (dateRange.from || dateRange.to) {
      list = list.filter((d) => {
        if (dateRange.from && d.date < dateRange.from) return false;
        if (dateRange.to && d.date > dateRange.to) return false;
        return true;
      });
    }
    return list;
  }, [callEod, putEod, dateRange.from, dateRange.to]);

  const tapeByDayChartData = useMemo((): TapeByDayChartRow[] => {
    if (!tapeByDay.length) return [];
    return tapeByDay.map((d) => {
      const ts = d.tape_summary || ({} as TapeSummary);
      return {
        date: d.date.length >= 10 ? d.date.slice(5) : d.date,
        dateFull: d.date,
        atBid: Number(ts.bid_side_contracts) || 0,
        atAsk: Number(ts.ask_side_contracts) || 0,
        between: Number(ts.between_contracts) || 0,
      };
    });
  }, [tapeByDay]);

  return {
    data,
    callEod,
    putEod,
    loading,
    error,
    dateRange,
    setDateRange,
    fetchForRange,
    handleUpdateDates,
    tapeByDay,
    tapeByDayLoading,
    thetaSecondOrder,
    thetaThirdOrder,
    loadingThetaGreeks,
    minDate,
    today,
    defaultFrom,
    chartData,
    filteredByDate,
    primaryChartData,
    primaryLabel,
    tradesChartData,
    deltaChartData,
    gammaChartData,
    spotGammaChartData,
    volatilityChartData,
    boughtSoldPctData,
    openClosePctData,
    flowChartData,
    tapeByDayChartData,
    unusualFlow,
    averages,
    effectiveSource,
    primaryMetricKey,
    todaySnapshot,
  };
}
